/**
 * Telehealth feature auto-registration and exports
 *
 * Automatically registers the telehealth feature when imported,
 * making it work seamlessly with both static and dynamic imports.
 */

import { autoRegisterFeature } from '@store/features/factory';

import { telehealthFeature } from './store/feature';

autoRegisterFeature(telehealthFeature);

export * from './constants';
export * from './store';
export * from './types';
export * from './waiting-room';

// Context
export { TelehealthCallManager } from './context/TelehealthCallManager';
export type {
  JoinCallParams,
  JoinWalkInCallParams,
  TelehealthCallManagerContextValue,
  TelehealthPhase,
} from './context/TelehealthCallManagerContext';
export { useTelehealthCallManager } from './context/useTelehealthCallManager';

// Hooks
export { useCallLayout } from './hooks/useCallLayout';
export { useFullscreen } from './hooks/useFullscreen';
export type { PipSizeClass } from './hooks/usePictureInPicture';
export { usePictureInPicture } from './hooks/usePictureInPicture';
export { useJotMeetAvailability } from './hooks/useJotMeetAvailability';
export { useTelehealthAccess } from './hooks/useTelehealthAccess';

// Components
export { ActiveCallElsewhereBanner } from './components/ActiveCallElsewhereBanner';
export { AudioLevelMeter } from './components/AudioLevelMeter';
export { CallPanel } from './components/CallPanel';
export { CallPanelPresentation } from './components/CallPanelPresentation';
export { CallTimer } from './components/CallTimer';
export { CameraDropdown } from './components/CameraDropdown';
export { ChatMessageBubble, SystemMessageRow } from './components/ChatMessageBubble';
export { ChatPanel } from './components/ChatPanel';
export { DeviceBadgeSelect } from './components/DeviceBadgeSelect';
export { EndCallDialog } from './components/EndCallDialog';
export { GridView } from './components/GridView';
export { InCallDeviceMenu } from './components/InCallDeviceMenu';
export { LayoutToggle } from './components/LayoutToggle';
export { MicDropdown } from './components/MicDropdown';
export { ParticipantTile } from './components/ParticipantTile';
export { CompactPipView } from './components/pip/CompactPipView';
export { PipView } from './components/pip/PipView';
export { PipViewPresentation } from './components/pip/PipViewPresentation';
export { PipActiveState } from './components/PipActiveState';
export { PipErrorBoundary } from './components/PipErrorBoundary';
export { PreCallLobby } from './components/PreCallLobby';
export { RecordingStatusBar } from './components/RecordingStatusBar';
export { SpeakerView } from './components/SpeakerView';
export { SplitMediaButton } from './components/SplitMediaButton';
export { TelehealthActiveSessionCard } from './components/TelehealthActiveSessionCard';
export { TelehealthContainer } from './components/TelehealthContainer';
export { TelehealthContainerSkeleton } from './components/TelehealthContainerSkeleton';
export { TelehealthJoinBox } from './components/TelehealthJoinBox';
export { TelehealthLinkPreview } from './components/TelehealthLinkPreview';
export {
  CenteredName,
  MicIndicator,
  MicLevelIndicator,
  ParticipantBadge,
} from './components/VideoBadges';
export { VideoSpotlight } from './components/VideoSpotlight';
